const SK_MONTHS = [
  'január',
  'február',
  'marec',
  'apríl',
  'máj',
  'jún',
  'júl',
  'august',
  'september',
  'október',
  'november',
  'december',
];

export function formatSkDate(date: Date): string {
  return `${date.getDate()}. ${SK_MONTHS[date.getMonth()]} ${date.getFullYear()}`;
}

export function formatSkDateTime(date: Date): string {
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${formatSkDate(date)} ${hours}:${minutes}`;
}

export function formatSkDateRange(arrival: Date, departure: Date): string {
  const sameMonthAndYear =
    arrival.getMonth() === departure.getMonth() &&
    arrival.getFullYear() === departure.getFullYear();

  if (sameMonthAndYear) {
    return `${arrival.getDate()}. – ${departure.getDate()}. ${SK_MONTHS[departure.getMonth()]} ${departure.getFullYear()}`;
  }

  return `${formatSkDate(arrival)} – ${formatSkDate(departure)}`;
}

export function countNights(arrival: Date, departure: Date): number {
  return Math.round((departure.getTime() - arrival.getTime()) / (1000 * 60 * 60 * 24));
}

export function pluralGuests(count: number): string {
  if (count === 1) return '1 osoba';
  if (count >= 2 && count <= 4) return `${count} osoby`;
  return `${count} osôb`;
}

export function pluralNights(count: number): string {
  if (count === 1) return '1 noc';
  if (count >= 2 && count <= 4) return `${count} noci`;
  return `${count} nocí`;
}

export const CITY_TAX_PER_PERSON_PER_NIGHT = 0.5;
export const DEPOSIT_RATE = 0.3;

// Only property id 2 has client-requested per-guest-count nightly pricing
// (2 → 66€, 3 → 95€, 4 → 125€/noc/byt). Every other property keeps using the
// single flat `price_per_night` value the backend returns.
const TIERED_PRICING_PROPERTY_ID = 2;
const GUEST_COUNT_NIGHTLY_PRICE: Record<number, number> = {
  2: 66,
  3: 95,
  4: 125,
};

export function computeHousingNightlyPrice({
  propertyId,
  guestsCount,
  flatPricePerNight,
}: {
  propertyId: number;
  guestsCount: number;
  flatPricePerNight: number;
}): number {
  if (propertyId !== TIERED_PRICING_PROPERTY_ID) return flatPricePerNight;

  const tierGuests = Math.min(Math.max(guestsCount, 2), 4);
  return GUEST_COUNT_NIGHTLY_PRICE[tierGuests] ?? flatPricePerNight;
}

export function computeReservationPricing({
  nights,
  guestsCount,
  pricePerNight,
}: {
  nights: number;
  guestsCount: number;
  pricePerNight: number;
}) {
  const accommodationTotal = nights * pricePerNight;
  const cityTaxTotal = nights * guestsCount * CITY_TAX_PER_PERSON_PER_NIGHT;
  const grandTotal = accommodationTotal + cityTaxTotal;
  const deposit = Math.round(grandTotal * DEPOSIT_RATE);
  return { accommodationTotal, cityTaxTotal, grandTotal, deposit };
}
